/**
 * Event Danke Page — /event/danke
 * Shown after successful Stripe payment for the business event.
 * 
 * URL params:
 *   ?session_id=cs_xxx  — Stripe Checkout Session ID (auto-filled by Stripe)
 *   ?name=Fatima+Khan   — fallback: manual name parameter
 * 
 * Stripe Success URL to configure:
 *   https://manus.ayu-rveda.ai/event/danke?session_id={CHECKOUT_SESSION_ID}
 */
import { useMemo, useRef, useState, useEffect } from "react";
import { useSearch } from "wouter";
import { toPng } from "html-to-image";
import {
  CheckCircle2,
  Calendar,
  Clock,
  MapPin,
  Download,
  ArrowLeft,
  Sparkles,
  Users,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";

const ANNAM_PHOTO =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/annam-business-danke_af9c5595.png";

const AYU_LOGO = "/branding/ayu-logo.svg";

export default function EventDanke() {
  const search = useSearch();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const sessionId = params.get("session_id") || "";
  const nameFromUrl = params.get("name") || "";

  // Fetch customer name from Stripe via session_id
  const { data: stripeData, isLoading: stripeLoading } =
    trpc.stripe.getCheckoutCustomer.useQuery(
      { sessionId },
      { enabled: !!sessionId, retry: false }
    );

  // Determine customer name: Stripe data > URL param > "Guest"
  const customerName = stripeData?.name || nameFromUrl || "Guest";
  const isLoading = !!sessionId && stripeLoading;

  const ticketRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  // Stable ticket number based on customer name
  const ticketNumber = useMemo(() => {
    if (isLoading || customerName === "Guest") return "------";
    let hash = 0;
    const str = customerName + "2026";
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash).toString(36).toUpperCase().substring(0, 6);
  }, [customerName, isLoading]);

  const handleDownload = async () => {
    if (!ticketRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(ticketRef.current, {
        cacheBust: true,
        pixelRatio: 3,
        backgroundColor: "#0d0b09",
      });
      const link = document.createElement("a");
      link.download = `ticket-annams-business-event-${customerName.replace(/\s+/g, "-").toLowerCase()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Ticket download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0d0b09] text-white"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#0d0b09]/90 backdrop-blur-md border-b border-[#c9a96e]/20">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <a href="/" className="text-[#c9a96e] font-semibold text-lg tracking-wide">
            AYU
          </a>
          <a
            href="/event"
            className="flex items-center gap-2 text-[#c9a96e] hover:text-[#d4b87a] transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Event
          </a>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-24 pb-16 px-4 max-w-2xl mx-auto">
        {/* Loading State */}
        {isLoading ? (
          <div className="text-center py-20">
            <Loader2 className="w-10 h-10 text-[#c9a96e] animate-spin mx-auto mb-4" />
            <p className="text-[#b5a890] text-lg">Loading your ticket...</p>
          </div>
        ) : (
          <>
            {/* Success Header */}
            <div className="text-center mb-10">
              <div className="w-20 h-20 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Thank You, {customerName}!
              </h1>
              <p className="text-[#b5a890] text-lg max-w-md mx-auto">
                Your registration is confirmed. Download your ticket below and bring it to the event.
              </p>
            </div>

            {/* ─── Ticket Card (for screenshot/download) ─── */}
            <div
              ref={ticketRef}
              className="relative bg-gradient-to-br from-[#1a1510] via-[#15120e] to-[#1a1510] rounded-2xl border border-[#c9a96e]/30 overflow-hidden"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {/* Gold top accent */}
              <div className="h-1.5 bg-gradient-to-r from-[#c9a96e] via-[#e8d5a8] to-[#c9a96e]" />

              {/* Ticket Header with photo */}
              <div className="relative p-6 pb-4 flex items-center gap-5">
                <img
                  src={ANNAM_PHOTO}
                  alt="Annam Hanfstein"
                  className="w-24 h-24 rounded-xl object-cover border-2 border-[#c9a96e]/30 shadow-lg"
                  crossOrigin="anonymous"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <img
                      src={AYU_LOGO}
                      alt="AYU"
                      className="h-6 w-auto"
                      crossOrigin="anonymous"
                    />
                    <span className="text-[#c9a96e]/60 text-xs">presents</span>
                  </div>
                  <h2 className="text-xl font-bold text-white leading-tight">
                    Annam's Business Event
                  </h2>
                  <p className="text-[#c9a96e] text-sm mt-1 font-medium">
                    Exclusive Desi Community Gathering
                  </p>
                </div>
              </div>

              {/* Dashed divider */}
              <div className="mx-6 border-t border-dashed border-[#c9a96e]/20" />

              {/* Ticket Body */}
              <div className="p-6 space-y-5">
                {/* Attendee Name — prominently displayed */}
                <div className="bg-[#0d0b09]/60 rounded-xl p-4 border border-[#c9a96e]/15">
                  <p className="text-[#7d7060] text-xs uppercase tracking-widest mb-1">Attendee</p>
                  <p className="text-2xl font-bold text-[#c9a96e]">{customerName}</p>
                </div>

                {/* Event Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#0d0b09]/40 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-[#c9a96e] mb-1">
                      <Calendar className="w-4 h-4" />
                      <span className="text-xs uppercase tracking-wider">Date</span>
                    </div>
                    <p className="text-white font-semibold">May 24, 2026</p>
                  </div>
                  <div className="bg-[#0d0b09]/40 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-[#c9a96e] mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-xs uppercase tracking-wider">Time</span>
                    </div>
                    <p className="text-white font-semibold">10:00 – 15:00</p>
                  </div>
                  <div className="col-span-2 bg-[#0d0b09]/40 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-[#c9a96e] mb-1">
                      <MapPin className="w-4 h-4" />
                      <span className="text-xs uppercase tracking-wider">Location</span>
                    </div>
                    <p className="text-white font-semibold">Near Darmstadt, Germany</p>
                    <p className="text-[#7d7060] text-xs mt-0.5">Exact address will be sent via email</p>
                  </div>
                </div>

                {/* Highlights */}
                <div className="flex items-center gap-3 text-[#b5a890] text-sm">
                  <Sparkles className="w-4 h-4 text-[#c9a96e] shrink-0" />
                  <span>Networking, Talks, Lunch & Desi Warehouse Launch</span>
                </div>
                <div className="flex items-center gap-3 text-[#b5a890] text-sm">
                  <Users className="w-4 h-4 text-[#c9a96e] shrink-0" />
                  <span>Adults only — No bad vibes, just good talks.</span>
                </div>
              </div>

              {/* Ticket Footer */}
              <div className="mx-6 border-t border-dashed border-[#c9a96e]/20" />
              <div className="p-4 flex items-center justify-between">
                <p className="text-[#7d7060] text-xs">
                  Organized by Annam Hanfstein
                </p>
                <p className="text-[#7d7060] text-xs font-mono">
                  TICKET-2026-{ticketNumber}
                </p>
              </div>
            </div>

            {/* Download Button */}
            <div className="mt-8 flex flex-col items-center gap-4">
              <Button
                onClick={handleDownload}
                disabled={downloading || customerName === "Guest"}
                className="bg-[#c9a96e] hover:bg-[#d4b87a] text-[#0d0b09] font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-[#c9a96e]/20 transition-all"
              >
                {downloading ? (
                  <>Generating...</>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Download Ticket
                  </>
                )}
              </Button>
              <p className="text-[#7d7060] text-sm text-center max-w-sm">
                Save this ticket as an image and present it at the event entrance.
              </p>
            </div>

            {/* Back to event */}
            <div className="mt-12 text-center">
              <a
                href="/event"
                className="inline-flex items-center gap-2 text-[#c9a96e] hover:text-[#d4b87a] transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Event Page
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
